
import React, { useEffect, useRef } from 'react';
import Heading from '@/components/ui/heading';
import BenefitCard from './BenefitCard';
import { Clock, DollarSign, Database } from 'lucide-react';

/**
 * ValueProposition component
 * Displays the main benefits of the product with animated cards and metrics
 */
const ValueProposition = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  
  // Define benefits data with metrics
  const benefits = [
    {
      icon: Clock,
      title: "3 Minutes vs 3 Days",
      description: "Generate comprehensive market intelligence in minutes, not days",
      metric: "97%",
      metricLabel: "time saved"
    },
    {
      icon: DollarSign,
      title: "90% Cost Reduction",
      description: "Enterprise-grade insights at a fraction of traditional costs",
      metric: "$100K+",
      metricLabel: "Annually"
    },
    {
      icon: Database,
      title: "Real-time Data",
      description: "Current market insights from multiple sources",
      metric: "60K+",
      metricLabel: "daily updates"
    }
  ];
  
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    cardsRef.current.forEach(card => {
      if (card) observer.observe(card);
    });
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      cardsRef.current.forEach(card => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-16 md:py-24 bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden opacity-0 transition-opacity duration-500"
    >
      {/* Enhanced background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gray-800/50 via-gray-900 to-gray-900"></div>
      <div className="absolute top-0 w-full h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
      
      <div className="container relative z-10 mx-auto px-4">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block py-1 px-3 text-sm font-medium bg-blue-900/40 text-blue-300 rounded-full backdrop-blur-sm border border-blue-800/50 mb-3 transform transition-transform duration-300 hover:scale-105">Why AnalystX</span>
          <Heading level={2} className="mb-5">
            Market Intelligence Reimagined
          </Heading>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            AnalystX combines cutting-edge AI with comprehensive data to deliver insights that transform decision-making.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
          {benefits.map((benefit, index) => {
            // Create a reference to the DOM node
            const cardRef = (el: HTMLDivElement | null) => cardsRef.current[index] = el;
            
            return (
              <BenefitCard
                key={index}
                ref={cardRef}
                icon={benefit.icon}
                title={benefit.title}
                description={benefit.description}
                metric={benefit.metric}
                metricLabel={benefit.metricLabel}
                index={index}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ValueProposition;
